import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva } from 'class-variance-authority';
import { cn } from '../utils';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipBase = TooltipPrimitive.Root;

TooltipBase.displayName = 'TooltipBase';

const TooltipTrigger = TooltipPrimitive.Trigger;

interface TooltipArrowProps
  extends React.ComponentProps<typeof TooltipPrimitive.Arrow> {
  className?: string;
}

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  TooltipArrowProps
>(({ width = 8, height = 4, ...props }, ref) => (
  <TooltipPrimitive.Arrow ref={ref} width={width} height={height} {...props} />
));

TooltipArrow.displayName = 'TooltipArrow';

const tooltipContentVariants = cva(
  'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-primary',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  }
);

const tooltipArrowVariants = {
  primary: 'fill-primary',
  secondary: 'fill-secondary',
} as const;

type TooltipContentVariant = 'primary' | 'secondary';

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: TooltipContentVariant;
    withArrow?: boolean;
    TooltipArrowProps?: TooltipArrowProps;
  }
>(
  (
    {
      className,
      sideOffset = 8,
      withArrow = true,
      TooltipArrowProps,
      variant = 'primary',
      ...props
    },
    ref
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ variant }), className)}
      {...props}
    >
      {props.children}
      {withArrow && (
        <TooltipArrow
          {...TooltipArrowProps}
          className={cn(
            tooltipArrowVariants[variant],
            TooltipArrowProps?.className
          )}
        />
      )}
    </TooltipPrimitive.Content>
  )
);

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface RichTooltipProps extends TooltipPrimitive.TooltipProps {
  content?: React.ReactNode;
  TooltipContentProps?: React.ComponentProps<typeof TooltipContent>;
  TooltipTriggerProps?: React.ComponentProps<typeof TooltipTrigger>;
}

const Tooltip: React.FC<RichTooltipProps> = ({
  children,
  content,
  TooltipContentProps = { variant: 'primary' },
  TooltipTriggerProps,
  ...props
}) => (
  <TooltipBase delayDuration={150} {...props}>
    <TooltipTrigger {...TooltipTriggerProps}>{children}</TooltipTrigger>
    {content && (
      <TooltipPrimitive.Portal>
        <TooltipContent {...TooltipContentProps}>{content}</TooltipContent>
      </TooltipPrimitive.Portal>
    )}
  </TooltipBase>
);

Tooltip.displayName = 'Tooltip';

export {
  Tooltip,
  TooltipBase,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  tooltipContentVariants,
};
